import React, { useState } from 'react';
import { useData } from '../../contexts/DataContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiTrendingUp, FiHome, FiBookOpen, FiFilter, FiCalendar, FiDollarSign } = FiIcons;

const CalculationHistory = () => {
  const { calculations } = useData();
  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date');

  const getTypeIcon = (type) => {
    switch (type) {
      case 'retirement': return FiTrendingUp;
      case 'home': return FiHome;
      case 'education': return FiBookOpen;
      default: return FiTrendingUp;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'retirement': return 'text-blue-600 bg-blue-100';
      case 'home': return 'text-green-600 bg-green-100';
      case 'education': return 'text-purple-600 bg-purple-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'retirement': return 'Retirement';
      case 'home': return 'Home Purchase';
      case 'education': return 'Education';
      default: return 'Unknown';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getKeyResult = (calculation) => {
    const { type, results } = calculation;
    
    switch (type) {
      case 'retirement':
        return `${formatCurrency(results.totalRetirementFund)} projected`;
      case 'home':
        return `${formatCurrency(results.monthlyPayment)}/month payment`;
      case 'education':
        return `${formatCurrency(results.totalEducationCost)} total cost`;
      default:
        return 'Calculation completed';
    }
  };

  const filteredCalculations = calculations
    .filter(calc => filter === 'all' || calc.type === filter)
    .sort((a, b) => {
      if (sortBy === 'date') {
        return new Date(b.createdAt) - new Date(a.createdAt);
      }
      return a.type.localeCompare(b.type);
    });

  return (
    <div className="space-y-6">
      {/* Header and Filters */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Calculation History</h2>
          <p className="text-gray-600">Review all your previous financial calculations</p>
        </div>
        
        <div className="flex space-x-4">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="all">All Types</option>
            <option value="retirement">Retirement</option>
            <option value="home">Home Purchase</option>
            <option value="education">Education</option>
          </select>
          
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="date">Sort by Date</option>
            <option value="type">Sort by Type</option>
          </select>
        </div>
      </div>

      {/* Calculations List */}
      {filteredCalculations.length > 0 ? (
        <div className="space-y-4">
          {filteredCalculations.map((calculation) => (
            <div key={calculation.id} className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-4">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${getTypeColor(calculation.type)}`}>
                    <SafeIcon icon={getTypeIcon(calculation.type)} className="w-6 h-6" />
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <h3 className="font-semibold text-gray-900">
                        {getTypeLabel(calculation.type)} Calculation
                      </h3>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(calculation.type)}`}>
                        {getTypeLabel(calculation.type)}
                      </span>
                    </div>
                    
                    <p className="text-gray-600 mb-2">
                      {getKeyResult(calculation)}
                    </p>
                    
                    <div className="flex items-center space-x-4 text-sm text-gray-500">
                      <div className="flex items-center space-x-1">
                        <SafeIcon icon={FiCalendar} className="w-4 h-4" />
                        <span>{new Date(calculation.createdAt).toLocaleDateString()}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <SafeIcon icon={FiDollarSign} className="w-4 h-4" />
                        <span>Financial Planning</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="flex flex-col items-end space-y-2">
                  <button className="text-primary-600 hover:text-primary-700 text-sm font-medium transition-colors">
                    View Details
                  </button>
                  <div className="text-xs text-gray-500">
                    {new Date(calculation.createdAt).toLocaleTimeString()}
                  </div>
                </div>
              </div>
              
              {/* Key Metrics Preview */}
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {calculation.type === 'retirement' && (
                    <>
                      <div className="text-center">
                        <p className="text-sm text-gray-500">Current Age</p>
                        <p className="font-semibold">{calculation.inputs.currentAge}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-sm text-gray-500">Retirement Age</p>
                        <p className="font-semibold">{calculation.inputs.retirementAge}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-sm text-gray-500">Monthly Contribution</p>
                        <p className="font-semibold">${calculation.inputs.monthlyContribution}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-sm text-gray-500">Expected Return</p>
                        <p className="font-semibold">{calculation.inputs.annualReturn}%</p>
                      </div>
                    </>
                  )}
                  
                  {calculation.type === 'home' && (
                    <>
                      <div className="text-center">
                        <p className="text-sm text-gray-500">Home Price</p>
                        <p className="font-semibold">${parseInt(calculation.inputs.homePrice).toLocaleString()}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-sm text-gray-500">Down Payment</p>
                        <p className="font-semibold">{calculation.inputs.downPayment}%</p>
                      </div>
                      <div className="text-center">
                        <p className="text-sm text-gray-500">Interest Rate</p>
                        <p className="font-semibold">{calculation.inputs.interestRate}%</p>
                      </div>
                      <div className="text-center">
                        <p className="text-sm text-gray-500">Loan Term</p>
                        <p className="font-semibold">{calculation.inputs.loanTerm} years</p>
                      </div>
                    </>
                  )}
                  
                  {calculation.type === 'education' && (
                    <>
                      <div className="text-center">
                        <p className="text-sm text-gray-500">Current Age</p>
                        <p className="font-semibold">{calculation.inputs.currentAge}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-sm text-gray-500">College Age</p>
                        <p className="font-semibold">{calculation.inputs.collegeAge}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-sm text-gray-500">Monthly Savings</p>
                        <p className="font-semibold">${calculation.inputs.monthlyContribution}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-sm text-gray-500">Years in College</p>
                        <p className="font-semibold">{calculation.inputs.yearsInCollege}</p>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-lg p-12 text-center">
          <SafeIcon icon={FiTrendingUp} className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            No Calculations Yet
          </h3>
          <p className="text-gray-600 mb-6">
            Start using our calculators to see your calculation history here
          </p>
          <div className="flex justify-center space-x-4">
            <a
              href="/calculators/retirement"
              className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
            >
              Start Planning
            </a>
          </div>
        </div>
      )}
      
      {/* Summary Stats */}
      {calculations.length > 0 && (
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Summary Statistics</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <p className="text-2xl font-bold text-primary-600">
                {calculations.length}
              </p>
              <p className="text-sm text-gray-600">Total Calculations</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-green-600">
                {calculations.filter(c => c.type === 'retirement').length}
              </p>
              <p className="text-sm text-gray-600">Retirement Plans</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-purple-600">
                {new Set(calculations.map(c => new Date(c.createdAt).toDateString())).size}
              </p>
              <p className="text-sm text-gray-600">Active Days</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalculationHistory;